/** Browser-only booking completion hint. Never use this as booking evidence. */
export interface BookingUxHint {
  provider: "calendly";
}

function calendlyHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === "calendly.com" || host.endsWith(".calendly.com");
}

/**
 * Calendly documents `calendly.event_scheduled` for its iframe embed. Accept it
 * only from the exact iframe mounted by Floe and a Calendly HTTPS origin. The
 * message payload can contain invitee details, so return a closed, PII-free
 * hint and discard the provider payload wholesale.
 */
export function bookingUxHintFromMessage(input: {
  event: MessageEvent;
  iframe: HTMLIFrameElement | null;
  calendarUrl: string | null;
}): BookingUxHint | null {
  if (!input.iframe?.contentWindow || !input.calendarUrl) return null;
  if (input.event.source !== input.iframe.contentWindow) return null;

  try {
    const calendar = new URL(input.calendarUrl);
    const origin = new URL(input.event.origin);
    if (
      calendar.protocol !== "https:" ||
      !calendlyHost(calendar.hostname) ||
      origin.protocol !== "https:" ||
      !calendlyHost(origin.hostname)
    ) {
      return null;
    }
  } catch {
    return null;
  }

  const data = input.event.data;
  if (
    !data ||
    typeof data !== "object" ||
    Array.isArray(data) ||
    (data as Record<string, unknown>).event !== "calendly.event_scheduled"
  ) {
    return null;
  }
  return { provider: "calendly" };
}
